import { useCallback, useEffect, useRef, useState } from "react";

import {
  generateClientId,
  offlineDb,
  type OfflineEventRecord,
  requireCachedUserId,
} from "@/lib/offline/db";
import { syncService, type SyncStatus } from "@/lib/offline/syncService";

export interface UseOfflineEventRecordsResult {
  isOffline: boolean;
  syncStatus: { pendingCount: number; failedCount: number };
  /**
   * Submit an event record. Works offline — the record is written to IndexedDB
   * and enqueued; the server create (and its manager notification) fires at
   * sync time. Returns the local client id so the caller can navigate / toast.
   */
  createEventRecord: (params: {
    eventTypeId: string;
    eventTypeName: string;
    data: Record<string, unknown>;
    /** People to ask to acknowledge this record (NAU-342). */
    acknowledgementRequestUserIds?: string[];
  }) => Promise<string>;
}

/**
 * Offline-first submission hook for event records.
 *
 * Event records are immutable and the staff/leader capture surface does not
 * render a history list (managers review on the dashboard), so unlike
 * `useOfflineLogEntries` this hook is intentionally create-only: no server
 * list is cached or merged. It mirrors that hook's write-and-enqueue flow and
 * sync-status tracking so a fire drill captured offline syncs on reconnect,
 * following the existing checklist offline pattern.
 */
export function useOfflineEventRecords(
  nurseryId: string | null,
): UseOfflineEventRecordsResult {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [syncStatus, setSyncStatus] = useState({
    pendingCount: 0,
    failedCount: 0,
  });

  const nurseryIdRef = useRef(nurseryId);
  nurseryIdRef.current = nurseryId;

  // Online/offline listeners
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Track sync status for queued event records (re-checked whenever the sync
  // service emits, e.g. after a successful flush on reconnect).
  const refreshSyncStatus = useCallback(async () => {
    const queueItems = await offlineDb.syncQueue
      .filter((i) => i.type === "eventRecord")
      .toArray();
    setSyncStatus({
      pendingCount: queueItems.filter((i) => i.attempts < 5).length,
      failedCount: queueItems.filter((i) => i.attempts >= 5).length,
    });
  }, []);

  useEffect(() => {
    refreshSyncStatus();
    const unsubscribe = syncService.subscribe((_status: SyncStatus) => {
      refreshSyncStatus();
    });
    return () => {
      unsubscribe();
    };
  }, [refreshSyncStatus]);

  const createEventRecord = useCallback(
    async (params: {
      eventTypeId: string;
      eventTypeName: string;
      data: Record<string, unknown>;
      acknowledgementRequestUserIds?: string[];
    }): Promise<string> => {
      const currentNurseryId = nurseryIdRef.current;
      if (!currentNurseryId) {
        throw new Error("No nursery selected");
      }

      // Cache the submitter's display name (mirrors the log-entry hook) so a
      // pending row can be shown without a fetch if a list is ever added.
      const userSession = await offlineDb.userSession.get("current-user");
      const userName = userSession
        ? [userSession.firstName, userSession.lastName]
            .filter(Boolean)
            .join(" ") ||
          userSession.email ||
          "You"
        : "You";

      const record: OfflineEventRecord = {
        id: generateClientId(),
        nurseryId: currentNurseryId,
        eventTypeId: params.eventTypeId,
        eventTypeName: params.eventTypeName,
        data: params.data,
        timestamp: new Date().toISOString(),
        // No empty-string fallback: the sync layer drops a falsy author, which
        // would silently replay this event record under whoever drains the queue —
        // exactly the mis-attribution NAU-399 exists to prevent. A missing
        // cached session means we cannot say who is recording, so refuse
        // rather than capture something we know will be attributed wrongly.
        recordedByUserId: requireCachedUserId(userSession?.userId),
        recordedByName: userName,
        syncState: "pending",
        acknowledgementRequestUserIds: params.acknowledgementRequestUserIds,
      };

      await offlineDb.eventRecords.add(record);
      await offlineDb.syncQueue.add({
        id: generateClientId(),
        type: "eventRecord",
        entityId: record.id,
        priority: 3,
        createdAt: Date.now(),
        attempts: 0,
      });

      await refreshSyncStatus();

      if (navigator.onLine) {
        syncService.debouncedSync();
      }

      return record.id;
    },
    [refreshSyncStatus],
  );

  return { isOffline, syncStatus, createEventRecord };
}
